import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, IonContent, ModalController, ActionSheetController } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { StatusBar } from '@capacitor/status-bar';
import { AvatarService } from 'src/app/services/avatar.service';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

@Component({
  selector: 'app-enroute-chat',
  templateUrl: './enroute-chat.component.html',
  styleUrls: ['./enroute-chat.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule]
})
export class EnrouteChatComponent implements OnInit, OnDestroy {

  @ViewChild(IonContent, { static: false }) content: IonContent;
  @Input() chatData: any;

  newMsg = '';
  messages: Observable<import("@angular/fire/firestore").DocumentData[]>;
  hasNoData = false;
  isLoading = true;
  currentLanguage: string = 'en';
  private messageSubscription: Subscription;
  isTyping = false;

  constructor(
    private chatService: AvatarService,
    private router: Router,
    private translate: TranslateService,
    private modalCtrl: ModalController,
    private actionSheetCtrl: ActionSheetController
  ) {
    this.currentLanguage = this.translate.currentLang || 'en';
  }

  ngOnInit() {
    console.log('Chat initialized with:', this.chatData);
    this.loadMessages();
  }

  ngOnDestroy() {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
  }

  loadMessages() {
    if (!this.chatData?.userId) {
      console.error('No userId provided to load messages');
      this.hasNoData = true;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.messages = this.chatService.getChatMessage(this.chatData.userId);

    this.messageSubscription = this.messages.pipe(
      tap(messages => {
        this.isLoading = false;
        if (messages.length === 0) {
          this.hasNoData = true;
        } else {
          this.hasNoData = false;
          setTimeout(() => this.scrollToBottom(), 300);
        }
      })
    ).subscribe();
  }

  async scrollToBottom() {
    try {
      await this.content.scrollToBottom(300);
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  async Show() {
    await StatusBar.setOverlaysWebView({ overlay: false });
  }

  async Hide() {
    await StatusBar.setOverlaysWebView({ overlay: true });
  }

  async sendMessage() {
    if (!this.newMsg.trim()) return;

    try {
      this.isTyping = true;
      await this.chatService.addChatEnRouteMessage(this.newMsg.trim(), this.chatData.userId);
      this.newMsg = '';
      await this.scrollToBottom();
      await this.chatService.updatChatMessageInfo(this.chatData.userId);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      this.isTyping = false;
    }
  }

  async presentAttachmentOptions() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Share Media',
      buttons: [
        {
          text: 'Camera',
          icon: 'camera',
          handler: () => this.takePhoto(CameraSource.Camera)
        },
        {
          text: 'Gallery',
          icon: 'image',
          handler: () => this.takePhoto(CameraSource.Photos)
        },
        {
          text: 'Voicenote',
          icon: 'mic',
          handler: () => this.recordAudio()
        },
        {
          text: 'Cancel',
          role: 'cancel'
        }
      ]
    });
    await actionSheet.present();
  }

  async takePhoto(source: CameraSource) {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source
      });

      if (image && image.base64String) {
        this.isLoading = true;
        const fileName = `img_${Date.now()}.jpg`;
        const url = await this.chatService.uploadChatMedia(image.base64String, this.chatData.userId, fileName);
        await this.chatService.addChatMediaMessage(this.chatData.userId, url, 'image');
        await this.chatService.updatChatMessageInfo(this.chatData.userId);
        this.scrollToBottom();
      }
    } catch (e) {
      console.error('Error taking photo:', e);
    } finally {
      this.isLoading = false;
    }
  }

  async recordAudio() {
    // For now, since native recording might require extra plugins, 
    // we'll implement a basic Web API approach or placeholder
    console.log('Audio recording not fully implemented - requires plugin');
  }

  playAudio(url: string) {
    const audio = new Audio(url);
    audio.play();
  }

  viewMedia(url: string, type: string) {
    // Implement full screen viewer if desired
    window.open(url, '_blank');
  }

  setLanguage(lang: string) {
    this.currentLanguage = lang;
    this.translate.use(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('preferred_language', lang);
  }

  closeChat() {
    this.modalCtrl.dismiss();
  }

  formatTime(timestamp: any): string {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    // Less than 24 hours, show time only
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // More than 24 hours, show date and time
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
